import * as React from 'react';
import { PencilIcon, CheckIcon, XMarkIcon, ExclamationTriangleIcon, PlusIcon } from '@heroicons/react/24/solid';
import { apiUrl, authFetch, authService } from '../utils/api';
import { useUser } from '../contexts/UserContext';
import { P } from '../types/assistantPrivileges';

interface ContactInfo {
  id?: number;
  whatsappNumber: string;
  facebookPage: string;
  youtubePage: string;
  tiktokChannel: string;
}

const emptyContact: ContactInfo = {
  whatsappNumber: '',
  facebookPage: '',
  youtubePage: '',
  tiktokChannel: '',
};

/** Map API Contact entity / DTO response into UI shape */
const mapContactFromApi = (data: any): ContactInfo => ({
  id: data.id,
  whatsappNumber: data.whatsApp_Number || '',
  facebookPage: data.facebook_Page || '',
  youtubePage: data.youTube_Channel || data.youTubeChannel || '',
  tiktokChannel: data.tiktokChannel || '',
});

/** Payload expected by AddContactDto */
const toContactPayload = (info: ContactInfo) => ({
  whatsApp_Number: info.whatsappNumber.trim(),
  facebook_Page: info.facebookPage.trim(),
  youTubeChannel: info.youtubePage.trim(),
  tiktokChannel: info.tiktokChannel.trim(),
});

const ContactUs: React.FC = () => {
  const { hasPrivilege } = useUser();
  const canEditContact = hasPrivilege(P.Contact_Reply);
  const [contactInfo, setContactInfo] = React.useState<ContactInfo>(emptyContact);
  const [isEditing, setIsEditing] = React.useState(false);
  const [editedInfo, setEditedInfo] = React.useState<ContactInfo>(emptyContact);
  const [isLoading, setIsLoading] = React.useState(true);
  const [isSaving, setIsSaving] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [saveMessage, setSaveMessage] = React.useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const hasContact = Boolean(contactInfo.id);

  const fetchContactInfo = React.useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await authFetch(apiUrl('Contact/getAll?pageNumber=1&pageSize=10'), {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authService.getToken()}`,
        },
      });

      const data = await response.json();

      if (data.success && Array.isArray(data.data) && data.data.length > 0) {
        setContactInfo(mapContactFromApi(data.data[0]));
      } else {
        // No contacts yet — allow create
        setContactInfo(emptyContact);
      }
    } catch (err) {
      console.error('Error fetching contact information:', err);
      setError('فشل تحميل بيانات التواصل. يرجى المحاولة مرة أخرى.');
    } finally {
      setIsLoading(false);
    }
  }, []);

  React.useEffect(() => {
    fetchContactInfo();
  }, [fetchContactInfo]);

  React.useEffect(() => {
    if (isEditing) {
      setEditedInfo({ ...contactInfo });
    }
  }, [isEditing, contactInfo]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setEditedInfo((prev) => ({ ...prev, [name]: value }));
  };

  const startEditing = () => {
    setSaveMessage(null);
    setIsEditing(true);
  };

  const cancelEditing = () => {
    setIsEditing(false);
    setSaveMessage(null);
  };

  const validateUrl = (url: string) => {
    if (!url) return true;
    try {
      new URL(url);
      return true;
    } catch {
      return false;
    }
  };

  const validateWhatsApp = (number: string) => {
    if (!number) return true;
    return /^\+?[0-9\s\-()]+$/.test(number);
  };

  const isFormValid =
    validateWhatsApp(editedInfo.whatsappNumber) &&
    validateUrl(editedInfo.facebookPage) &&
    validateUrl(editedInfo.youtubePage) &&
    validateUrl(editedInfo.tiktokChannel);

  const saveChanges = async () => {
    if (!isFormValid) return;

    setIsSaving(true);
    setSaveMessage(null);

    try {
      const isCreate = !contactInfo.id;
      const url = isCreate
        ? apiUrl('Contact/add')
        : apiUrl(`Contact/update/${contactInfo.id}`);

      const response = await authFetch(url, {
        method: isCreate ? 'POST' : 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authService.getToken()}`,
        },
        body: JSON.stringify(toContactPayload(editedInfo)),
      });

      const data = await response.json();

      if (data.success && data.data) {
        setContactInfo(mapContactFromApi(data.data));
        setSaveMessage({
          type: 'success',
          text: isCreate ? 'تم إضافة بيانات التواصل بنجاح' : 'تم تحديث بيانات التواصل بنجاح',
        });
        setIsEditing(false);
      } else {
        setSaveMessage({
          type: 'error',
          text: data.message || (isCreate ? 'فشل إضافة بيانات التواصل' : 'فشل تحديث بيانات التواصل'),
        });
      }
    } catch (err) {
      console.error('Error saving contact information:', err);
      setSaveMessage({
        type: 'error',
        text: 'فشل حفظ بيانات التواصل. يرجى المحاولة مرة أخرى.',
      });
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="container mx-auto px-4 py-8 flex justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8" dir="rtl">
      <h1 className="text-2xl font-bold mb-6">اتصل بنا</h1>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <div className="flex">
            <ExclamationTriangleIcon className="h-5 w-5 text-red-400 flex-shrink-0" aria-hidden="true" />
            <div className="mr-3">
              <h3 className="text-sm font-medium text-red-800">خطأ</h3>
              <p className="mt-2 text-sm text-red-700">{error}</p>
              <button
                type="button"
                onClick={fetchContactInfo}
                className="mt-2 text-sm text-red-800 underline"
              >
                إعادة المحاولة
              </button>
            </div>
          </div>
        </div>
      )}

      {saveMessage && (
        <div
          className={`mb-6 p-4 rounded-lg ${
            saveMessage.type === 'success' ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'
          }`}
        >
          <div className="flex items-center gap-2">
            {saveMessage.type === 'success' ? (
              <CheckIcon className="h-5 w-5 text-green-400" aria-hidden="true" />
            ) : (
              <ExclamationTriangleIcon className="h-5 w-5 text-red-400" aria-hidden="true" />
            )}
            <p className="text-sm font-medium">{saveMessage.text}</p>
          </div>
        </div>
      )}

      <div className="bg-white rounded-lg shadow-md p-6">
        {isEditing ? (
          <>
            <div className="space-y-6">
              <div>
                <label htmlFor="whatsappNumber" className="block text-sm font-medium text-gray-700 mb-1">
                  رقم واتساب
                </label>
                <input
                  type="text"
                  id="whatsappNumber"
                  name="whatsappNumber"
                  value={editedInfo.whatsappNumber}
                  onChange={handleInputChange}
                  placeholder="مثال: +201234567890"
                  className={`mt-1 block w-full rounded-md shadow-sm focus:ring focus:ring-opacity-50 ${
                    !validateWhatsApp(editedInfo.whatsappNumber)
                      ? 'border-red-500 focus:border-red-500 focus:ring-red-500'
                      : 'border-gray-300 focus:border-blue-500 focus:ring-blue-500'
                  }`}
                />
                {!validateWhatsApp(editedInfo.whatsappNumber) && (
                  <p className="mt-1 text-sm text-red-600">يرجى إدخال رقم هاتف صحيح</p>
                )}
              </div>

              <div>
                <label htmlFor="facebookPage" className="block text-sm font-medium text-gray-700 mb-1">
                  رابط صفحة فيسبوك
                </label>
                <input
                  type="text"
                  id="facebookPage"
                  name="facebookPage"
                  value={editedInfo.facebookPage}
                  onChange={handleInputChange}
                  placeholder="https://facebook.com/yourpage"
                  className={`mt-1 block w-full rounded-md shadow-sm focus:ring focus:ring-opacity-50 ${
                    !validateUrl(editedInfo.facebookPage)
                      ? 'border-red-500 focus:border-red-500 focus:ring-red-500'
                      : 'border-gray-300 focus:border-blue-500 focus:ring-blue-500'
                  }`}
                />
                {!validateUrl(editedInfo.facebookPage) && (
                  <p className="mt-1 text-sm text-red-600">يرجى إدخال رابط صحيح</p>
                )}
              </div>

              <div>
                <label htmlFor="youtubePage" className="block text-sm font-medium text-gray-700 mb-1">
                  رابط قناة يوتيوب
                </label>
                <input
                  type="text"
                  id="youtubePage"
                  name="youtubePage"
                  value={editedInfo.youtubePage}
                  onChange={handleInputChange}
                  placeholder="https://youtube.com/c/yourchannel"
                  className={`mt-1 block w-full rounded-md shadow-sm focus:ring focus:ring-opacity-50 ${
                    !validateUrl(editedInfo.youtubePage)
                      ? 'border-red-500 focus:border-red-500 focus:ring-red-500'
                      : 'border-gray-300 focus:border-blue-500 focus:ring-blue-500'
                  }`}
                />
                {!validateUrl(editedInfo.youtubePage) && (
                  <p className="mt-1 text-sm text-red-600">يرجى إدخال رابط صحيح</p>
                )}
              </div>

              <div>
                <label htmlFor="tiktokChannel" className="block text-sm font-medium text-gray-700 mb-1">
                  رابط قناة تيك توك
                </label>
                <input
                  type="text"
                  id="tiktokChannel"
                  name="tiktokChannel"
                  value={editedInfo.tiktokChannel}
                  onChange={handleInputChange}
                  placeholder="https://tiktok.com/@username"
                  className={`mt-1 block w-full rounded-md shadow-sm focus:ring focus:ring-opacity-50 ${
                    !validateUrl(editedInfo.tiktokChannel)
                      ? 'border-red-500 focus:border-red-500 focus:ring-red-500'
                      : 'border-gray-300 focus:border-blue-500 focus:ring-blue-500'
                  }`}
                />
                {!validateUrl(editedInfo.tiktokChannel) && (
                  <p className="mt-1 text-sm text-red-600">يرجى إدخال رابط صحيح</p>
                )}
              </div>
            </div>

            <div className="mt-6 flex gap-3">
              <button
                type="button"
                onClick={saveChanges}
                disabled={isSaving || !isFormValid}
                className="flex items-center justify-center px-4 py-2 rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSaving ? (
                  'جاري الحفظ...'
                ) : (
                  <>
                    <CheckIcon className="h-5 w-5 ml-2" />
                    {hasContact ? 'حفظ التعديلات' : 'إضافة'}
                  </>
                )}
              </button>

              <button
                type="button"
                onClick={cancelEditing}
                disabled={isSaving}
                className="flex items-center justify-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <XMarkIcon className="h-5 w-5 ml-2" />
                إلغاء
              </button>
            </div>
          </>
        ) : (
          <>
            {!hasContact ? (
              <div className="text-center py-8">
                <p className="text-gray-500 mb-4">لا توجد بيانات تواصل بعد. أضف روابط التواصل الاجتماعي ورقم واتساب.</p>
                {canEditContact && (
                <button
                  type="button"
                  onClick={startEditing}
                  className="inline-flex items-center justify-center px-4 py-2 rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  <PlusIcon className="h-5 w-5 ml-2" />
                  إضافة بيانات التواصل
                </button>
                )}
              </div>
            ) : (
              <>
                <div className="space-y-6">
                  <div>
                    <h3 className="text-sm font-medium text-gray-500">رقم واتساب</h3>
                    <p className="mt-1 text-lg">
                      {contactInfo.whatsappNumber || (
                        <span className="text-gray-400 italic">غير محدد</span>
                      )}
                    </p>
                  </div>

                  <div>
                    <h3 className="text-sm font-medium text-gray-500">صفحة فيسبوك</h3>
                    {contactInfo.facebookPage ? (
                      <a
                        href={contactInfo.facebookPage}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="mt-1 text-lg text-blue-600 hover:underline block break-all"
                      >
                        {contactInfo.facebookPage}
                      </a>
                    ) : (
                      <p className="mt-1 text-lg text-gray-400 italic">غير محدد</p>
                    )}
                  </div>

                  <div>
                    <h3 className="text-sm font-medium text-gray-500">قناة يوتيوب</h3>
                    {contactInfo.youtubePage ? (
                      <a
                        href={contactInfo.youtubePage}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="mt-1 text-lg text-blue-600 hover:underline block break-all"
                      >
                        {contactInfo.youtubePage}
                      </a>
                    ) : (
                      <p className="mt-1 text-lg text-gray-400 italic">غير محدد</p>
                    )}
                  </div>

                  <div>
                    <h3 className="text-sm font-medium text-gray-500">قناة تيك توك</h3>
                    {contactInfo.tiktokChannel ? (
                      <a
                        href={contactInfo.tiktokChannel}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="mt-1 text-lg text-blue-600 hover:underline block break-all"
                      >
                        {contactInfo.tiktokChannel}
                      </a>
                    ) : (
                      <p className="mt-1 text-lg text-gray-400 italic">غير محدد</p>
                    )}
                  </div>
                </div>

                {canEditContact && (
                <button
                  type="button"
                  onClick={startEditing}
                  className="mt-6 flex items-center justify-center px-4 py-2 rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  <PencilIcon className="h-5 w-5 ml-2" />
                  تعديل بيانات التواصل
                </button>
                )}
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default ContactUs;
